import React, { useMemo, useState } from 'react';
import Twin3DCanvas from './Twin3DCanvas';
import Twin3DControls from './Twin3DControls';
import Twin3DPanel from './Twin3DPanel';
import { mapPayloadToScene } from './unitMapper';
import { DEFAULT_THEME } from './themes';
import './twin-3d.css';

/**
 * Twin3DView — orchestrates the 3D viewer for a Digital Twin payload.
 *
 * Wraps the canvas, controls, and selected-unit bento panel. Owns viewer state
 * (theme/heatmap/grid/labels/glass + selection). Drop this anywhere a backend
 * twin payload is available:
 *
 *   <Twin3DView propertyId={id} payload={twinPayload} floor={1} />
 *
 *  - propertyId   needed for the per-unit drilldown API call
 *  - payload      raw response from GET /api/twin/:propertyId (the .data block)
 *  - floor        optional filter; defaults to all floors
 */
export default function Twin3DView({ propertyId, payload, floor }) {
    const [theme, setTheme] = useState(DEFAULT_THEME);
    const [heatmap, setHeatmap] = useState('off');
    const [showGrid, setShowGrid] = useState(true);
    const [showLabels, setShowLabels] = useState(true);
    const [selected, setSelected] = useState(null);

    const scene = useMemo(() => mapPayloadToScene(payload, floor), [payload, floor]);

    return (
        <div className="twin3d-root">
            <Twin3DCanvas
                units={scene.units}
                planArea={scene.planArea}
                floorPlanUrl={scene.floorPlan}
                theme={theme}
                heatmap={heatmap}
                showGrid={showGrid}
                showLabels={showLabels}
                onSelectUnit={setSelected}
            />
            {selected && (
                <Twin3DPanel
                    propertyId={propertyId}
                    sceneUnit={selected}
                    onClose={() => setSelected(null)}
                />
            )}
            <Twin3DControls
                theme={theme} setTheme={setTheme}
                heatmap={heatmap} setHeatmap={setHeatmap}
                showGrid={showGrid} setShowGrid={setShowGrid}
                showLabels={showLabels} setShowLabels={setShowLabels}
            />
        </div>
    );
}
